import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { MdStorm } from "react-icons/md";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { getSerenityColor } from '../../utils/serenityColors';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import { useTranslation } from 'react-i18next';
import { deleteImageRecord, updateImageFavorite, type ImageDoc } from '../../services/gallery';
import { deleteImage } from '../../services/images';

interface MonthlyOverviewProps {
  dreams: Dream[];
  onDelete: (id: string) => void;
  onKeywordClick: (keyword: string) => void;
  imagesByDreamId: Record<string, ImageDoc>;
  onImageUpdated: (dreamId: string, image: ImageDoc | null) => void;
}

interface Dream {
  id: string;
  text: string;
  emotions: string;
  analysis: string;
  keywords: string[];
  serenityScore: number;
  createdAt: Date;
  approaches: Array<{
    categoryName: string;
    subcategoryName: string;
  }>;
}

export default function MonthlyOverview({ dreams, onDelete, onKeywordClick, imagesByDreamId, onImageUpdated }: MonthlyOverviewProps) {
  const { t, i18n } = useTranslation();
  const [selectedDream, setSelectedDream] = useState<Dream | null>(null);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'es':
        return es;
      default:
        return enUS;
    }
  };

  const formatMonth = (date: Date) => {
    return format(date, t('dreamJournal.monthlyOverview.monthFormat'), {
      locale: getLocale()
    });
  };

  const formatCompactDate = (date: Date) => {
    return format(date, t('dreamJournal.monthlyOverview.compactDateFormat'), {
      locale: getLocale()
    });
  };

  // Fonction pour calculer la hauteur approximative du texte
  const isTextLong = (text: string) => {
    const averageCharsPerLine = 50; // Estimation moyenne de caractères par ligne
    const lines = text.length / averageCharsPerLine;
    return lines > 5;
  };

  const monthlyData = useMemo(() => {
    const data: { [key: string]: Dream[] } = {};

    // Grouper les rêves par mois
    dreams.forEach(dream => {
      const monthKey = format(dream.createdAt, 'yyyy-MM');
      if (!data[monthKey]) {
        data[monthKey] = [];
      }
      data[monthKey].push(dream);
    });

    // Calculer les statistiques pour chaque mois
    return Object.entries(data).map(([monthKey, monthDreams]) => {
      const averageSerenity = monthDreams.reduce((acc, dream) => acc + dream.serenityScore, 0) / monthDreams.length;

      // Compter les occurrences des mots-clés
      const keywordCount: { [key: string]: number } = {};
      monthDreams.forEach(dream => {
        dream.keywords.forEach(keyword => {
          keywordCount[keyword] = (keywordCount[keyword] || 0) + 1;
        });
      });

      // Ne garder que les mots-clés qui apparaissent au moins 2 fois
      const recurringKeywords = Object.entries(keywordCount)
        .filter(([_, count]) => count >= 2)
        .map(([keyword, count]) => ({ keyword, count }))
        .sort((a, b) => b.count - a.count);

      const emotionsCount: { [key: string]: number } = {};
      monthDreams.forEach(dream => {
        if (dream.emotions) {
          const emotions = dream.emotions.split(',').map(e => e.trim());
          emotions.forEach(emotion => {
            emotionsCount[emotion] = (emotionsCount[emotion] || 0) + 1;
          });
        }
      });

      const dominantEmotions = Object.entries(emotionsCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([emotion]) => emotion);

      return {
        month: formatMonth(new Date(monthKey + '-01')),
        monthKey,
        dreams: monthDreams.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
        totalDreams: monthDreams.length,
        averageSerenity,
        dominantEmotions,
        recurringKeywords,
      };
    }).sort((a, b) => new Date(b.monthKey).getTime() - new Date(a.monthKey).getTime());
  }, [dreams]);

  const handleOpenAnalysis = (dream: Dream) => {
    setPreviewOpen(false);
    setDeleteDialogOpen(false);
    setIsUpdatingFavorite(false);
    setShowFullDescription(false);
    setExpandedDescription(false);
    setSelectedDream(dream);
  };

  const handleCloseAnalysis = () => {
    setPreviewOpen(false);
    setDeleteDialogOpen(false);
    setIsUpdatingFavorite(false);
    setSelectedDream(null);
  };

  const handleDeleteSelectedDream = async () => {
    if (!selectedDream) return;
    try {
      await onDelete(selectedDream.id);
    } finally {
      handleCloseAnalysis();
    }
  };

  const selectedImage = useMemo(() => {
    if (!selectedDream) return null;
    return imagesByDreamId[selectedDream.id] ?? null;
  }, [selectedDream, imagesByDreamId]);

  const handleOpenPreview = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!selectedImage) return;
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const toggleFavorite = async () => {
    if (!selectedDream || !selectedImage || isUpdatingFavorite) return;
    setIsUpdatingFavorite(true);
    try {
      await updateImageFavorite(selectedImage.id, !selectedImage.favorite);
      onImageUpdated(selectedDream.id, { ...selectedImage, favorite: !selectedImage.favorite });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du favori:', error);
    } finally {
      setIsUpdatingFavorite(false);
    }
  };

  const openDeleteConfirmation = (event?: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    if (!selectedImage) return;
    setDeleteDialogOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteImage = async () => {
    if (!selectedDream || !selectedImage) return;
    try {
      closeDeleteConfirmation();
      setPreviewOpen(false);
      if (selectedImage.fileId || selectedImage.filePath || selectedImage.url) {
        await deleteImage({
          fileId: selectedImage.fileId || undefined,
          filePath: selectedImage.filePath || undefined,
          url: selectedImage.url
        });
      }
      await deleteImageRecord(selectedImage.id);
      onImageUpdated(selectedDream.id, null);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
    }
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleDownload = (monthData: any) => {
    const content = monthData.dreams.map((dream: Dream) => `
Date: ${format(dream.createdAt, 'dd MMMM yyyy', { locale: getLocale() })}

Description:
${dream.text}

Émotions:
${dream.emotions}

Analyse:
${dream.analysis}

Niveau de sérénité: ${dream.serenityScore}%

Mots-clés: ${dream.keywords.join(', ')}

-------------------
`).join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `journal-reves-${monthData.month}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {monthlyData.map((monthData) => (
        <Accordion 
          key={monthData.monthKey}
          sx={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            borderRadius: '20px !important',
            mb: 1,
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#68f1d1' }} />}
            sx={{
              borderBottom: '0px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                gap: { xs: 1, sm: 2 }, 
                width: '100%',
                justifyContent: { sm: 'flex-start' }
              }}>
              {/* Première ligne (date et nombre de rêves) */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon sx={{ color: 'black' }} />
                  <Typography sx={{ color: 'black', fontWeight: 500, fontSize: '14px' }}>
                    {monthData.month}
                  </Typography>
                </Box>
                <Chip 
                  label={`${monthData.totalDreams} ${t('dreamJournal.monthlyOverview.dreamCount').toLowerCase()}`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(55, 55, 55, 0.3)',
                    color: 'white',
                  }}
                />
              </Box>
              
              {/* Niveau de sérénité */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                ml: { xs: 1, sm: 3 },
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', whiteSpace: 'nowrap' }}>
                  {t('dreamJournal.monthlyOverview.averageSerenity')}: 
                </Typography>
                <Typography sx={{ 
                  color: getSerenityColor(monthData.averageSerenity),
                  fontWeight: 'bold'
                }}>
                  {Math.round(monthData.averageSerenity)}%
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
            overflow: 'hidden',
            background: 'var(--fd-gridDreams)',
          }}>
            {/* Statistiques mensuelles */}
            <Box sx={{ mb: 3 }}>
              {monthData.recurringKeywords.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#68f1d1', mb: 1 }}>
                    {t('dreamJournal.monthlyOverview.recurringKeywords')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {monthData.recurringKeywords.map(({ keyword, count }) => (
                      <Chip
                        key={keyword}
                        label={`${keyword} (${count})`}
                        size="small"
                        onClick={() => onKeywordClick(keyword)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            cursor: 'pointer'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Grille des rêves -------------------------------------------------------------------*/}
            <Grid container spacing={2} >
              {monthData.dreams.map((dream) => (
                <Grid item xs={12} sm={6} md={4} key={dream.id} >
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      background: 'rgba(101, 157, 254, 0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '15px',
                      boxShadow: 'inset 0 4px 12px rgba(0, 0, 0, 0.52)',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                      }
                    }}
                    onClick={() => handleOpenAnalysis(dream)}
                  >
                    <CardContent sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          {formatCompactDate(dream.createdAt)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SelfImprovementIcon style={{ color: getSerenityColor(dream.serenityScore) }} />
                          <Typography variant="body2" sx={{ color: getSerenityColor(dream.serenityScore), mr: 1, ml: 0.5 }}>
                            {dream.serenityScore}%
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(dream.id);
                            }}
                            sx={{
                              color: 'rgba(255, 99, 99, 0.8)',
                              '&:hover': {
                                color: '#ff6363',
                                backgroundColor: 'rgba(255, 99, 99, 0.2)'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 2,
                          color: 'rgba(255, 255, 255, 0.9)',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.4
                        }}
                      >
                        {truncateText(dream.text, 100)}
                      </Typography>

                      {dream.keywords.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {dream.keywords.slice(0, 3).map((keyword, index) => (
                            <Chip
                              key={index}
                              label={keyword}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(104, 241, 209, 0.1)',
                                color: '#68f1d1',
                                height: '20px',
                                fontSize: '0.8rem'
                              }}
                            />
                          ))}
                          {dream.keywords.length > 3 && (
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              +{dream.keywords.length - 3}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(monthData)}
                sx={{
                  color: 'white',
                  background : 'var(--primary-color)',
                  opacity : 0.6,
                  '&:hover': {
                    backgroundColor: 'var(--primary-color)',
                    opacity : 1
                  }
                }}
              >
                {t('dreamJournal.monthlyOverview.downloadMonth')}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Dialog d'analyse détaillée!!!!!!!!!!!!!!!!!!------------------------------ */}
      <Dialog
        open={!!selectedDream}
        onClose={handleCloseAnalysis}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--Pages-background6)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            borderRadius: '20px',
            maxHeight: '98vh',
            minWidth: '320px',
            maxWidth: '1000px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {selectedDream && (
          <>
            <DialogTitle sx={{ 
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              p: 2,
              pb: 1,
              mb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ color: '#68f1d1' }} />
                <Typography>
                  {format(selectedDream.createdAt, 'dd MMMM yyyy', { locale: getLocale() })}
                </Typography>
              </Box>
              <IconButton
                onClick={handleDeleteSelectedDream}
                sx={{ color: 'white', '&:hover': { color: '#ff6363' } }}
              >
                <DeleteIcon />
              </IconButton>
            </DialogTitle>

            {/* Section défilante avec le contenu */}
            <DialogContent sx={{ 
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'linear-gradient(180deg, #68f1d1 0%, #FF4B8C 100%)',
                  borderRadius: '4px',
                },
              }}>

              {/* Section fixe avec les cartes */}
              <Box sx={{ p: 0, pt: 1, mb: 2 }}>
                <Grid container spacing={1} sx={{ mb: 0 }}>
                  {/* Colonne gauche : Rêve + sérénité */}
                  <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', flexGrow: 1 }}>
                      <Paper 
                        elevation={6}
                        onClick={() => selectedDream && setShowFullDescription(true)}
                        sx={{
                          p: 2,
                          flexGrow: 1,
                          background: 'linear-gradient(155deg, rgba(128, 48, 97, 0.78) -10%, rgba(202, 90, 129, 0.8) 90%)',
                          borderRadius: '10px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
                          }
                        }}
                      >
                        <Typography variant="body1" sx={{ 
                          mt: -1,
                          mb: 0,
                          color: '#68f1d1',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 500
                        }}>
                          {t('dreamJournal.monthlyOverview.description')}
                        </Typography>
                        {selectedDream && (
                          <Typography sx={{ 
                            color: 'rgba(255, 255, 255, 0.9)',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                            lineHeight: 1.4
                          }}>
                            {truncateText(selectedDream.text, 80)}
                          </Typography>
                        )}
                      </Paper>

                      <Paper 
                        elevation={6}
                        sx={{
                          p: 2,
                          minHeight: 120,
                          background: 'linear-gradient(155deg, rgba(104, 241, 209, 0.1) -10%, rgba(255, 75, 140, 0.1) 60%)',
                          borderRadius: '10px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        <Typography variant="body1" sx={{ 
                          mt: -1,
                          mb: 1,
                          color: '#68f1d1',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 500
                        }}>
                          {t('dreamJournal.monthlyOverview.serenityLevel')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography sx={{ 
                            color: getSerenityColor(selectedDream.serenityScore),
                            fontWeight: 'bold',
                            fontSize: '1.2rem'
                          }}>
                            {selectedDream.serenityScore}%
                          </Typography>
                          <Box sx={{ flexGrow: 1, position: 'relative' }}>
                            <Box
                              sx={{
                                width: '100%',
                                height: '10px',
                                borderRadius: '5px',
                                background: 'linear-gradient(90deg, rgba(104, 241, 209, 0.2) 0%, rgba(255, 99, 99, 0.2) 100%)',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: `${selectedDream.serenityScore}%`,
                                height: '10px',
                                borderRadius: '5px',
                                background: 'linear-gradient(90deg, #68f1d1 0%, #ff6363 100%)',
                              }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>

                  {/* Colonne droite : Vignette sur deux lignes */}
                  <Grid item xs={12} md={6} >
                    <Paper
                      elevation={6}
                      sx={{
                        p: 2,
                        height: '100%',
                        minHeight: { md: 220 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(155deg, rgba(104, 241, 209, 0.01) -10%, rgba(255, 75, 140, 0.1) 90%)',
                        borderRadius: '10px',
                        //boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      {selectedImage ? (
                        <Box
                          onClick={handleOpenPreview}
                          sx={{
                            width: 200,
                            height: 200,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            //border: '3px solid rgba(15, 15, 16, 0.74)',
                            //boxShadow: '0 12px 32px rgba(0,0,0,0.45)'
                          }}
                        >
                          <img
                            src={selectedImage.url}
                            alt={selectedImage.name || 'dream-thumbnail'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 200,
                            height: 200,
                            borderRadius: '16px',
                            border: '2px dashed rgba(255,255,255,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.9rem',
                            textAlign: 'center',
                            px: 2
                          }}
                        >
                          {t('dreamJournal.dreamDetail.noImageForDream')}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Mots-clés */}
                  <Grid item xs={12} sm={9} md={9}>
                    <Paper 
                      elevation={6}
                      sx={{
                        p: 2,
                        maxHeight: '100px',
                        height: '100%',
                        background: 'linear-gradient(155deg, rgba(104, 241, 209, 0.1) -10%, rgba(255, 75, 140, 0.1) 60%)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <Typography variant="body1" sx={{
                        mt: -1.5, 
                        mb: 1,
                        color: '#68f1d1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 500
                      }}>
                        {t('dreamJournal.monthlyOverview.keywords')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedDream.keywords.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(104, 241, 209, 0.1)',
                              color: '#68f1d1',
                              '&:hover': {
                                backgroundColor: 'rgba(104, 241, 209, 0.2)',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>

                  {/* emotions */}
                  <Grid item xs={12} sm={3} md={3}>
                    <Paper 
                      elevation={6}
                      sx={{
                        p: 2,
                        maxHeight: '100px',
                        height: '100%',
                        background: 'linear-gradient(155deg, rgba(104, 241, 209, 0.1) -10%, rgba(255, 75, 140, 0.1) 60%)',
                        borderRadius: '10px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      <Typography variant="body1" sx={{ 
                        mt: -1.5,
                        mb: 1,
                        color: '#68f1d1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 500
                      }}>
                      {t('dreamJournal.monthlyOverview.emotions')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedDream.emotions.split(',').map((emotion, index) => (
                        <Chip
                          key={index}
                          label={emotion.trim()}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            }
                          }}
                        />
                      ))}
                    </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              
                

                {/* Analyse en 3 colonnes */}
                <Box>
                  <Typography variant="h6" sx={{ 
                    mb: 2,
                    color: '#68f1d1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 500
                  }}>
                    {t('dreamJournal.monthlyOverview.analysis')}
                  </Typography>
                  <Box
                    sx={{
                      columnCount: { xs: 1, sm: 2, md: 3 },
                      columnGap: '2rem',
                      columnRule: '1px solid rgba(255, 55, 55, 0.2)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        mb: 5
                      }}
                    >
                      {selectedDream.analysis.split('\n\n').slice(0, -1).join('\n\n')}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#68f1d1',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        fontStyle: 'italic',
                        mb: 10
                      }}
                    >
                      {selectedDream.analysis.split('\n\n').slice(-1)[0]}
                    </Typography>
                  </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
              p: 2,
              mt: 'auto'
            }}>
              <Button 
                onClick={handleCloseAnalysis}
                sx={{
                  background: 'var(--ConnectButton)',
                  opacity: 0.5,
                  color: 'black',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    background: 'var(--ConnectButton)',
                    opacity: 1
                  }
                }}
              >
                {t('dreamJournal.monthlyOverview.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog pour afficher l'image ------------- lightbox---------------*/}
      <Dialog
        open={previewOpen && !!selectedImage}
        onClose={handleClosePreview}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0.3, sm: 0.5, md: 0.8, lg: 1 },
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'min(1024px, 100vw)',
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        {selectedImage && (
          <Box
            sx={{
              position: 'relative',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'stretch' },
              gap: { xs: 2, sm: 2.5, md: 3 },
              p: { xs: 2, sm: 2.5, md: 3 }
            }}
          >
            <Box
              sx={{
                order: { xs: 1, md: 2 },
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 72 },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                justifyContent: { xs: 'flex-end', md: 'flex-start' },
                alignItems: { xs: 'center', md: 'flex-end' },
                gap: 1,
                pr: { xs: 0.5, sm: 1, md: 0 }
              }}
            >
              <IconButton
                onClick={handleClosePreview}
                sx={{ color: 'white', backgroundColor: 'var(--home-subtitle)', '&:hover': { backgroundColor: 'var(--primary-color)' } }}
                aria-label={t('common.close')}
              >
                <CloseIcon />
              </IconButton>

              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  toggleFavorite();
                }}
                disabled={isUpdatingFavorite}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: selectedImage.favorite ? 'var(--red)' : 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' }
                }}
                aria-label={selectedImage.favorite ? t('gallery.controls.tooltipRemoveFavorite') : t('gallery.controls.tooltipAddFavorite')}
              >
                {selectedImage.favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              </IconButton>

              <IconButton
                onClick={(event) => openDeleteConfirmation(event)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' }
                }}
                aria-label={t('gallery.controls.tooltipDelete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                order: { xs: 2, md: 1 },
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pb: { xs: 2, sm: 2, md: 0 }
              }}
            >
              <img
                src={selectedImage.url}
                alt={selectedImage.name || 'preview'}
                style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '1024px', maxHeight: '100vh' }}
              />
            </Box>

            <Box sx={{ order: 3, display: { xs: 'block', md: 'none' }, width: '100%', height: 48 }} />
          </Box>
        )}
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteConfirmation}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(12, 22, 48, 0.98)',
            borderRadius: 0.5,
            boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
            px: 3,
            py: 2.5,
            minWidth: 280
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ fontFamily: 'Comfortaa', color: 'white', fontWeight: 600 }}>
          {t('gallery.controls.deleteConfirmTitle')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.75)' }}>
          {t('gallery.controls.deleteConfirmDescription')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.6)' }}>
          {t('gallery.controls.deleteConfirmDreamRetention')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button onClick={handleDeleteImage} sx={{ color: 'var(--primary-color)' }}>
            {t('gallery.controls.deleteButton')}
          </Button>
          <Button onClick={closeDeleteConfirmation} sx={{ color: 'rgba(255,255,255,0.75)' }}>
            {t('gallery.controls.cancelButton')}
          </Button>
        </Box>
      </Dialog>

      {/* Dialogue pour le texte complet du rêve-------------------------- */}
      {selectedDream && (
        <Dialog
          open={showFullDescription}
          onClose={() => setShowFullDescription(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'var(--Pages-background)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              borderRadius: '15px',
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#68f1d1'
          }}>
            {t('dreamJournal.monthlyOverview.fullDescription')}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6
            }}>
              {selectedDream.text}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
            p: 2
          }}>
            <Button 
              onClick={() => setShowFullDescription(false)}
              sx={{
                background: 'var(--primary-color)',
                opacity: 0.5,
                color: 'black',
                px: 3,
                py: 1,
                borderRadius: '15px',
                textTransform: 'none',
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              {t('dreamJournal.monthlyOverview.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
